import type { MediaRequest, RequestSeasonSummary } from './types'

export type RequestQueueSection = 'attention' | 'waiting' | 'working' | 'finished'

export function requestQueueSection(item: MediaRequest, canReview: boolean): RequestQueueSection {
  if (item.status === 'Available' && item.mediaType === 'Tv' &&
    item.seasonSummaries?.some(season => !season.isAvailable)) return 'waiting'
  if (['Available', 'Declined', 'Cancelled'].includes(item.status)) return 'finished'
  if (item.status === 'PendingApproval' && canReview) return 'attention'
  if (item.isFutureRelease) return 'waiting'
  if (item.status === 'Failed') return 'attention'
  if (item.status === 'PendingApproval') return 'waiting'
  if (item.status === 'Queued' || item.status === 'Downloading') return 'working'
  if (item.status === 'PartiallyAvailable') {
    const remaining = (item.seasonSummaries ?? []).filter(season => !season.isAvailable)
    return remaining.length > 0 && remaining.every(season => season.nextAirDate || season.knownEpisodeCount === 0)
      ? 'waiting' : 'working'
  }
  return 'waiting'
}

export function requestReleaseBadge(item: MediaRequest): string | null {
  if (item.isFutureRelease) return 'Upcoming'
  const seasons = item.seasonSummaries ?? []
  const remaining = seasons.filter(season => !season.isAvailable)
  if (remaining.some(season => season.isActivelyAiring && season.availableEpisodeCount > 0)) return 'Airing'
  if (remaining.some(season => season.nextAirDate)) return 'Upcoming'
  if (remaining.some(season => season.isActivelyAiring)) return 'Airing'
  if (remaining.length > 0 && seasons.some(season => season.isAvailable)) return 'Between seasons'
  return null
}

export function requestSeasonText(season: RequestSeasonSummary, isFutureRelease = false): string {
  if (season.isAvailable) return season.knownEpisodeCount > 0
    ? `${season.availableEpisodeCount}/${season.knownEpisodeCount} available` : 'available'
  if (season.nextAirDate) {
    const nextDate = new Date(season.nextAirDate)
    const date = nextDate.toLocaleDateString(undefined, {
      month: 'short', day: 'numeric',
      ...(nextDate.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' as const } : {})
    })
    return season.availableEpisodeCount > 0
      ? `${season.availableEpisodeCount}/${season.knownEpisodeCount} available · next ${date}`
      : `starts ${date}`
  }
  if (isFutureRelease) return 'awaiting premiere'
  if (season.knownEpisodeCount > 0) return `${season.availableEpisodeCount}/${season.knownEpisodeCount} available`
  return 'awaiting schedule'
}
